import type {
  AppState,
  AuthUser,
  Building,
  CategoryConfig,
  MarkerCategory,
  ProjectListItem,
  ProjectMeta,
  ShapeCategory,
  ShapeKind,
  StatePayload,
} from "./types";

/** Set by main.ts; invoked when any API call returns 401 (session expired). */
let onUnauthorized: (() => void) | null = null;
export function setUnauthorizedHandler(fn: () => void): void {
  onUnauthorized = fn;
}

async function json<T>(res: Response): Promise<T> {
  if (res.status === 401) {
    onUnauthorized?.();
    throw new Error("Nicht angemeldet.");
  }
  if (!res.ok) {
    let msg = `${res.status} ${res.statusText}`;
    try {
      const body = (await res.json()) as { error?: string };
      if (body?.error) msg = body.error;
    } catch {
      /* ignore */
    }
    throw new Error(msg);
  }
  return (await res.json()) as T;
}

const jsonHeaders = { "content-type": "application/json" };

export const api = {
  /* ------------------------------ auth ------------------------------ */

  async me(): Promise<{ user: AuthUser | null; needsSetup: boolean }> {
    return json(await fetch("/api/auth/me"));
  },
  async setup(username: string, password: string): Promise<AuthUser> {
    return (
      await json<{ user: AuthUser }>(
        await fetch("/api/auth/setup", {
          method: "POST",
          headers: jsonHeaders,
          body: JSON.stringify({ username, password }),
        })
      )
    ).user;
  },
  async login(username: string, password: string): Promise<AuthUser> {
    return (
      await json<{ user: AuthUser }>(
        await fetch("/api/auth/login", {
          method: "POST",
          headers: jsonHeaders,
          body: JSON.stringify({ username, password }),
        })
      )
    ).user;
  },
  async logout(): Promise<void> {
    await fetch("/api/auth/logout", { method: "POST" });
  },

  /* ------------------------------ users ----------------------------- */

  async listUsers(): Promise<Array<{ id: string; username: string; createdAt: string }>> {
    return (await json<{ users: Array<{ id: string; username: string; createdAt: string }> }>(
      await fetch("/api/users")
    )).users;
  },
  async createUser(username: string, password: string): Promise<{ id: string; username: string }> {
    return (
      await json<{ user: { id: string; username: string } }>(
        await fetch("/api/users", {
          method: "POST",
          headers: jsonHeaders,
          body: JSON.stringify({ username, password }),
        })
      )
    ).user;
  },
  async setUserPassword(id: string, password: string): Promise<void> {
    await json(
      await fetch(`/api/users/${id}/password`, {
        method: "PATCH",
        headers: jsonHeaders,
        body: JSON.stringify({ password }),
      })
    );
  },
  async deleteUser(id: string): Promise<void> {
    await json(await fetch(`/api/users/${id}`, { method: "DELETE" }));
  },

  /* ---------------------------- projects ---------------------------- */

  async listProjects(): Promise<ProjectListItem[]> {
    return (await json<{ projects: ProjectListItem[] }>(await fetch("/api/projects"))).projects;
  },
  async createProject(form: FormData): Promise<ProjectMeta> {
    return (
      await json<{ project: ProjectMeta }>(
        await fetch("/api/projects", { method: "POST", body: form })
      )
    ).project;
  },
  async getState(id: string): Promise<AppState> {
    return json<AppState>(await fetch(`/api/projects/${id}`));
  },
  async saveState(id: string, payload: StatePayload): Promise<AppState> {
    return json<AppState>(
      await fetch(`/api/projects/${id}/state`, {
        method: "PUT",
        headers: jsonHeaders,
        body: JSON.stringify(payload),
      })
    );
  },
  async patchProject(
    id: string,
    patch: { name?: string; buildingId?: string | null; floorId?: string | null }
  ): Promise<ProjectMeta> {
    return (
      await json<{ project: ProjectMeta }>(
        await fetch(`/api/projects/${id}`, {
          method: "PATCH",
          headers: jsonHeaders,
          body: JSON.stringify(patch),
        })
      )
    ).project;
  },
  async deleteProject(id: string): Promise<void> {
    await json<{ ok: boolean }>(await fetch(`/api/projects/${id}`, { method: "DELETE" }));
  },
  exportUrl(id: string): string {
    return `/api/projects/${id}/export`;
  },
  async createShareLink(id: string): Promise<ProjectMeta> {
    return (
      await json<{ project: ProjectMeta }>(await fetch(`/api/projects/${id}/share`, { method: "POST" }))
    ).project;
  },
  async revokeShareLink(id: string): Promise<ProjectMeta> {
    return (
      await json<{ project: ProjectMeta }>(await fetch(`/api/projects/${id}/share`, { method: "DELETE" }))
    ).project;
  },
  async getPublicShare(token: string): Promise<AppState & { config: CategoryConfig }> {
    return json<AppState & { config: CategoryConfig }>(await fetch(`/api/public/share/${token}`));
  },
  backupUrl(): string {
    return `/api/backup`;
  },
  async importState(id: string, data: unknown): Promise<AppState> {
    return json<AppState>(
      await fetch(`/api/projects/${id}/import`, {
        method: "POST",
        headers: jsonHeaders,
        body: JSON.stringify(data),
      })
    );
  },

  /* ---------------------------- assets ---------------------------- */

  async uploadImage(file: File): Promise<{ file: string; url: string }> {
    const form = new FormData();
    form.append("file", file);
    return json<{ file: string; url: string }>(
      await fetch("/api/assets", { method: "POST", body: form })
    );
  },

  /* --------------------------- buildings --------------------------- */

  async listBuildings(): Promise<Building[]> {
    return (await json<{ buildings: Building[] }>(await fetch("/api/buildings"))).buildings;
  },
  async createBuilding(name: string): Promise<Building> {
    return (
      await json<{ building: Building }>(
        await fetch("/api/buildings", { method: "POST", headers: jsonHeaders, body: JSON.stringify({ name }) })
      )
    ).building;
  },
  async renameBuilding(id: string, name: string): Promise<Building> {
    return (
      await json<{ building: Building }>(
        await fetch(`/api/buildings/${id}`, { method: "PATCH", headers: jsonHeaders, body: JSON.stringify({ name }) })
      )
    ).building;
  },
  async deleteBuilding(id: string): Promise<void> {
    await json<{ ok: boolean }>(await fetch(`/api/buildings/${id}`, { method: "DELETE" }));
  },
  async addFloor(buildingId: string, name: string): Promise<Building> {
    return (
      await json<{ building: Building }>(
        await fetch(`/api/buildings/${buildingId}/floors`, {
          method: "POST",
          headers: jsonHeaders,
          body: JSON.stringify({ name }),
        })
      )
    ).building;
  },
  async updateFloor(id: string, patch: { name?: string; sortOrder?: number }): Promise<Building> {
    return (
      await json<{ building: Building }>(
        await fetch(`/api/floors/${id}`, { method: "PATCH", headers: jsonHeaders, body: JSON.stringify(patch) })
      )
    ).building;
  },
  async deleteFloor(id: string): Promise<void> {
    await json<{ ok: boolean }>(await fetch(`/api/floors/${id}`, { method: "DELETE" }));
  },

  /* --------------------------- categories --------------------------- */

  async createMarkerCategory(input: {
    label: string;
    color: string;
    shape: "circle" | "rect";
  }): Promise<MarkerCategory> {
    return (
      await json<{ category: MarkerCategory }>(
        await fetch("/api/marker-categories", {
          method: "POST",
          headers: jsonHeaders,
          body: JSON.stringify(input),
        })
      )
    ).category;
  },
  async updateMarkerCategory(
    id: string,
    patch: { label?: string; color?: string; shape?: "circle" | "rect"; imageFile?: string | null }
  ): Promise<MarkerCategory> {
    return (
      await json<{ category: MarkerCategory }>(
        await fetch(`/api/marker-categories/${id}`, {
          method: "PATCH",
          headers: jsonHeaders,
          body: JSON.stringify(patch),
        })
      )
    ).category;
  },
  async deleteMarkerCategory(id: string): Promise<void> {
    await json<{ ok: boolean }>(await fetch(`/api/marker-categories/${id}`, { method: "DELETE" }));
  },
  async createShapeCategory(input: {
    kind: ShapeKind;
    label: string;
    color: string;
  }): Promise<ShapeCategory> {
    return (
      await json<{ category: ShapeCategory }>(
        await fetch("/api/shape-categories", {
          method: "POST",
          headers: jsonHeaders,
          body: JSON.stringify(input),
        })
      )
    ).category;
  },
  async updateShapeCategory(
    id: string,
    patch: { label?: string; color?: string }
  ): Promise<ShapeCategory> {
    return (
      await json<{ category: ShapeCategory }>(
        await fetch(`/api/shape-categories/${id}`, {
          method: "PATCH",
          headers: jsonHeaders,
          body: JSON.stringify(patch),
        })
      )
    ).category;
  },
  async deleteShapeCategory(id: string): Promise<void> {
    await json<{ ok: boolean }>(await fetch(`/api/shape-categories/${id}`, { method: "DELETE" }));
  },
};
